import UIComponent from "sap/ui/core/UIComponent";
import JSONModel from "sap/ui/model/json/JSONModel";
import Localization from "sap/base/i18n/Localization";
import Theming from "sap/ui/core/Theming";
import IconPool from "sap/ui/core/IconPool";
import IllustrationPool from "sap/m/IllustrationPool";
import Device from "sap/ui/Device";
import Logger from "sap/base/Log";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

/**
 * Shared Services
 * Provides reusable initialization logic for all components
 * Can be used by Shell, App, and all package components
 *
 * @namespace niteroi.services
 */
export class SharedServices {
    /**
     * Initialize all shared services for a component
     * Call this from any component's init() method
     *
     * @param component The component instance to initialize services for
     */
    public static initializeAll(component: UIComponent): void {
        this.initializeLanguage(component);
        this.initializeTheme(component);
    }

    /**
     * Initialize language settings from system settings or browser
     * Shared across all components
     *
     * @param component The component instance
     */
    public static initializeLanguage(component: UIComponent): void {
        const storedLanguage = window.localStorage.getItem("userLanguage") || window.navigator.language || "en";
        const settingsModel = component.getModel("settings") as JSONModel;
        const sessionModel = component.getModel("session") as JSONModel;

        const normalized = this.normalizeLanguage(storedLanguage);
        Localization.setLanguage(normalized);
        window.localStorage.setItem("userLanguage", normalized);
        component.setModel(new JSONModel({language: normalized}), "systemLanguage");

        if (sessionModel) {
            sessionModel.dataLoaded().then(() => {
                const userLanguage = sessionModel.getProperty("/user/locale") as string;
                if (userLanguage) {
                    const normalized = this.normalizeLanguage(userLanguage);
                    Localization.setLanguage(normalized);
                    window.localStorage.setItem("userLanguage", normalized);
                    component.setModel(new JSONModel({language: normalized}), "systemLanguage");
                }
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                Logger.fatal("### Works: catching error on starting session", `${responseText.code} - ${responseText.error}`, "SharedServices.initializedLanguage");
            });
            return;
        }

        if (settingsModel) {
            settingsModel.attachRequestCompleted(() => {
                let language = settingsModel.getProperty("/systemLanguage") as string;

                if (!language && storedLanguage) {
                    language = storedLanguage;
                } else if (!language && !storedLanguage) {
                    language = window.navigator.language || "en";
                }

                language = this.normalizeLanguage(language);
                Localization.setLanguage(language);
                window.localStorage.setItem("userLanguage", language);
                component.setModel(new JSONModel({language: language}), "systemLanguage");
            });
        }
    }

    public static updateLanguage(language: string, component: UIComponent): void {
        const normalized = this.normalizeLanguage(language);
        Localization.setLanguage(normalized);
        window.localStorage.setItem("userLanguage", normalized);
        component.setModel(new JSONModel({language: normalized}), "systemLanguage");
    }

    /**
     * Normalize language codes to supported locales
     *
     * @param requestedLanguage The requested language code
     * @returns Normalized language code
     */
    public static normalizeLanguage(requestedLanguage: string): string {
        const supportedLanguages = ["en", "de", "pt-BR", "pt-PT"];

        if (supportedLanguages.includes(requestedLanguage)) {
            return requestedLanguage;
        }

        // Try to match by prefix (e.g., "pt" → "pt-BR")
        const prefix = requestedLanguage.split("-")[0];
        const match = supportedLanguages.find(lang => lang.startsWith(prefix));

        return match || "en";
    }

    /**
     * Initialize theme based on time of day (Berlin timezone)
     * Shared across all components
     *
     * @param component The component instance to set theme model on
     */
    public static initializeTheme(component: UIComponent): void {
        const sessionModel = component.getModel("session") as JSONModel;

        if (sessionModel && !component.getModel("theme")) {
            // Update theme when session data is loaded from backend
            const fnUpdateTheme = () => {
                this.updateTheme(component);
                this.initializeLanguage(component);
            };
            sessionModel.attachRequestCompleted(fnUpdateTheme);
        } else {
            // Initial theme setup (before session is loaded or if no session)
            this.updateTheme(component);
        }
    }

    /**
     * Update theme settings based on current session data or defaults
     * Can be called manually after login/session updates
     *
     * @param component The component instance
     */
    public static updateTheme(component: UIComponent): void {
        const berlinHour = new Intl.DateTimeFormat(
            window.localStorage.getItem("userLanguage") || "en",
            {
                timeZone: "Europe/Berlin",
                hour: "numeric",
                hour12: false,
            }
        ).format(new Date());

        const sessionModel = component.getModel("session") as JSONModel;
        let userTheme = window.localStorage.getItem("userTheme");

        if (sessionModel) {
            const sessionTheme = sessionModel.getProperty("/user/theme") as string;
            if (sessionTheme) {
                userTheme = sessionTheme;
                window.localStorage.setItem("userTheme", userTheme);
            }
        }

        const hour = parseInt(berlinHour, 10);
        const isDay = hour >= 6 && hour < 18;

        if (userTheme === "S" || !userTheme) {
            userTheme = isDay ? "sap_horizon" : "sap_horizon_dark";
        } else if (userTheme === "L") {
            userTheme = "sap_horizon";
        } else if (userTheme === "D") {
            userTheme = "sap_horizon_dark";
        } else if (userTheme === "W") {
            userTheme = "sap_horizon_hcw";
        } else if (userTheme === "B") {
            userTheme = "sap_horizon_hcb";
        }

        Logger.debug("### Works: Theme set to:", userTheme);

        const themeModel = new JSONModel({
            isDay: isDay,
            position: userTheme.endsWith("_dark") ? "End" : "Start",
            theme: userTheme,
        });
        component.setModel(themeModel, "theme");
        Theming.setTheme(userTheme);
    }

    public static getThemeName(themeKey: string): string {
        if (themeKey === "S") {
            return Theming.getTheme();
        }
        switch (themeKey) {
            case "L":
                return "sap_horizon";
            case "D":
                return "sap_horizon_dark";
            case "W":
                return "sap_horizon_hcw";
            case "B":
                return "sap_horizon_hcb";
            default:
                return Theming.getTheme();
        }
    }

    /**
     * Register icon libraries
     * Shared across all components
     */
    public static registerIconLibraries(): void {
        try {
            IconPool.registerFont({
                fontFamily: "SAP-icons-TNT",
                fontURI: sap.ui.require.toUrl("sap/tnt/themes/base/fonts/"),
                lazy: false
            });
            Logger.info("### Works: SAP-icons-TNT registered");
        } catch (error) {
            Logger.info("### Works: SAP-icons-TNT already registered", JSON.stringify(error));
        }

        try {
            IconPool.registerFont({
                fontFamily: "BusinessSuiteInAppSymbols",
                fontURI: sap.ui.require.toUrl("sap/ushell/themes/base/fonts/"),
                lazy: false
            });
            Logger.info("### Works: BusinessSuiteInAppSymbols registered");
        } catch (error) {
            Logger.info("### Works: BusinessSuiteInAppSymbols already registered", JSON.stringify(error));
        }
    }

    /**
     * Register illustration sets
     * Shared across all components
     */
    public static registerIllustrationSets(): void {
        const tntSet = {
            setFamily: "tnt",
            setURI: sap.ui.require.toUrl("sap/tnt/themes/base/illustrations"),
        };

        try {
            IllustrationPool.registerIllustrationSet(tntSet, false, []);
        } catch (error) {
            Logger.info("### Works: Illustration sets already registered", JSON.stringify(error));
        }
    }

    /**
     * Create device model
     * Shared across all components
     *
     * @returns Device model with OneWay binding
     */
    public static createDeviceModel(): JSONModel {
        const deviceModel = new JSONModel(Device);
        deviceModel.setDefaultBindingMode("OneWay");
        return deviceModel;
    }
}
